import { useEffect, useState, useRef, useMemo } from 'react';
import { useInView } from 'framer-motion';

export function useAnimatedCounter(target, duration = 2000) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const hasAnimated = useRef(false);

  // Parse prefix/suffix from target string (e.g. "$2.8M" → prefix="$", number=2.8, suffix="M")
  const parsed = useMemo(() => {
    const targetStr = String(target);
    const match = targetStr.match(/^([^0-9]*)([\d,.]+)(.*)$/);
    const numStr = match ? match[2].replace(/,/g, '') : '0';
    const hasCommas = match ? match[2].includes(',') : false;
    
    // Auto-detect decimals from the target string
    const decimals = numStr.includes('.') ? (numStr.split('.')[1]?.length || 0) : 0;
    
    return {
      prefix: match ? match[1] : '',
      suffix: match ? match[3] : '',
      numericTarget: parseFloat(numStr),
      hasCommas,
      decimals,
    };
  }, [target]);

  const { prefix, suffix, numericTarget, hasCommas, decimals } = parsed;

  // Start at 0, animate when in view
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView || hasAnimated.current) return;
    hasAnimated.current = true;

    if (isNaN(numericTarget)) return;

    const startTime = performance.now();
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(numericTarget * eased);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, numericTarget, duration]);

  // Format the number
  let formatted;
  if (decimals > 0) {
    formatted = Math.max(0, count).toFixed(decimals);
  } else {
    const rounded = Math.round(Math.max(0, count));
    formatted = hasCommas ? rounded.toLocaleString() : String(rounded);
  }

  return { ref, value: `${prefix}${formatted}${suffix}` };
}
